import { useEffect } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import moment from "moment";
import { CustomFormField } from "../../utilis/component/CustomFormElement";
import usePopup from "../../hooks/usePopup";
import usePrint from "../../hooks/usePrint";
import { InsuranceReceiverType } from "../types";
import {
  getClaimTemplateQatar,
  getPatientClaimInsuranceList,
} from "./services";

type PatientClaimListForm = {
  fromDate: string;
  toDate: string;
  insurance: InsuranceReceiverType | null;
};

const today = moment().format("YYYY-MM-DD");

const PatientClaimList = () => {
  const { showSnackbar } = usePopup();
  const { printPdf } = usePrint();
  const { control, handleSubmit, reset } = useForm<PatientClaimListForm>({
    defaultValues: {
      fromDate: today,
      toDate: today,
      insurance: null,
    },
  });

  const {
    data: insuranceData,
    isLoading: insuranceLoading,
    isError: insuranceError,
  } = useQuery(["patient-claim-insurance-list"], getPatientClaimInsuranceList, {
    refetchOnWindowFocus: false,
  });

  const { mutate: submitClaimTemplate, isLoading: submitLoading } = useMutation(
    getClaimTemplateQatar,
    {
      onSuccess: (result) => {
        if (result.responseIndicator === "success") {
          const fileContent =
            typeof result.response === "string"
              ? result.response
              : result.response?.file;

          if (fileContent) {
            printPdf(fileContent, "new-tab", "xlsx");
            showSnackbar({
              message:
                result.responseMessage || "Excel file downloaded successfully",
              open: true,
              variant: "success",
              duration: 3000,
            });
          } else {
            showSnackbar({
              message: "No Excel file found in the response",
              open: true,
              variant: "warning",
              duration: 3000,
            });
          }
        } else {
          showSnackbar({
            message: result.responseMessage || "Failed to generate claim list",
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (error: any) => {
        showSnackbar({
          message:
            error?.response?.data?.responseMessage ||
            "Something went wrong. Please try again.",
          open: true,
          variant: "error",
          duration: 3000,
        });
      },
    },
  );

  useEffect(() => {
    if (insuranceError) {
      showSnackbar({
        message: "Failed to load insurance list",
        open: true,
        variant: "warning",
        duration: 3000,
      });
    }
  }, [insuranceError, showSnackbar]);

  const onSubmit = (values: PatientClaimListForm) => {
    if (!values.fromDate || !values.toDate || !values.insurance?.id) {
      showSnackbar({
        message: "Please select from date, to date, and insurance",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    submitClaimTemplate({
      fromDate: values.fromDate,
      toDate: values.toDate,
      insuranceId: values.insurance.id,
    });
  };

  return (
    <div className="p-3">
      <Paper>
        <div className="p-3">
          <Typography variant="h6">Qatar Insurance Claim List</Typography>
        </div>
        <Divider />
        <form onSubmit={handleSubmit(onSubmit)} className="p-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
            <CustomFormField
              control={control}
              name="fromDate"
              element="datepicker"
              dateFormat="YYYY-MM-DD"
              fieldProps={{ label: "From date" }}
              rules={{ required: "From date is required" }}
            />
            <CustomFormField
              control={control}
              name="toDate"
              element="datepicker"
              dateFormat="YYYY-MM-DD"
              fieldProps={{ label: "To date" }}
              rules={{ required: "To date is required" }}
            />
            <CustomFormField
              control={control}
              name="insurance"
              element="autocomplete"
              fieldProps={{ label: "Insurance" }}
              rules={{ required: "Insurance is required" }}
              options={insuranceData?.response || []}
              loading={insuranceLoading}
              autocompleteProps={{
                getOptionLabel: (option: InsuranceReceiverType) =>
                  option?.name || "",
                isOptionEqualToValue: (
                  option: InsuranceReceiverType,
                  value: InsuranceReceiverType,
                ) => option.id === value.id,
                renderOption: (params, option: InsuranceReceiverType) => (
                  <ListItem {...params}>
                    <ListItemText primary={option.name} />
                  </ListItem>
                ),
              }}
            />
            <div className="flex gap-2 items-center h-full pt-1">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitLoading}
                startIcon={
                  submitLoading ? <CircularProgress size={18} /> : undefined
                }
              >
                Submit
              </Button>
              <Button
                type="button"
                color="error"
                variant="outlined"
                disabled={submitLoading}
                onClick={() =>
                  reset({
                    fromDate: today,
                    toDate: today,
                    insurance: null,
                  })
                }
              >
                Clear
              </Button>
            </div>
          </div>
        </form>
      </Paper>
    </div>
  );
};

export default PatientClaimList;
